import React from "react";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { SITE, isEarlyAccessConfigured } from "../../config/site";
import {
  getSessionId,
  resetSessionId,
  getUtmParams,
  getLocale,
  getPagePath,
  submitEarlyAccess,
} from "../../lib/earlyAccess";
import { loadTurnstile } from "../../lib/turnstile";

const USER_TYPES = [
  { value: "creator", label: "Creator" },
  { value: "freelancer", label: "Freelancer" },
  { value: "solopreneur", label: "Solopreneur" },
  { value: "agency", label: "Agency" },
  { value: "small_business", label: "Small business" },
  { value: "team_or_organisation", label: "Team or organisation" },
  { value: "other", label: "Other" },
];

const TEAM_REQUIRED_TYPES = ["agency", "small_business", "team_or_organisation"];

const MAIN_USE_CASES = [
  { value: "content_creation", label: "Content creation" },
  { value: "business_collaboration", label: "Business collaboration" },
  { value: "publishing_automation", label: "Publishing and automation" },
  { value: "client_team_workflows", label: "Client or team workflows" },
  { value: "exploring", label: "Exploring Culmora OS" },
  { value: "other", label: "Other" },
];

const TEAM_SIZES = [
  { value: "just_me", label: "Just me" },
  { value: "2-5", label: "2–5" },
  { value: "6-20", label: "6–20" },
  { value: "21-50", label: "21–50" },
  { value: "51+", label: "51+" },
];

const COUNTRIES = [
  { code: "AE", name: "United Arab Emirates" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "SG", name: "Singapore" },
  { code: "JP", name: "Japan" },
  { code: "BR", name: "Brazil" },
  { code: "ZA", name: "South Africa" },
  { code: "OTHER", name: "Other" },
];

const initial = {
  full_name: "",
  email: "",
  country_code: "",
  user_type: "",
  main_use_case: "",
  team_size: "",
  terms_accepted: false,
  privacy_accepted: false,
  marketing_opt_in: false,
  company: "", // honeypot — must stay empty
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RequestForm({ headingLevel = "h2" }) {
  const [values, setValues] = React.useState(initial);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [state, setState] = React.useState(null); // success | duplicate | rate_limited | unavailable | error
  const [termsReviewed, setTermsReviewed] = React.useState(false);
  const [privacyReviewed, setPrivacyReviewed] = React.useState(false);
  const startedAtRef = React.useRef(null);
  const turnstileRef = React.useRef(null);
  const turnstileTokenRef = React.useRef("");
  const widgetIdRef = React.useRef(null);
  const summaryRef = React.useRef(null);

  const configured = isEarlyAccessConfigured();
  const Heading = headingLevel === "h1" ? "h1" : "h2";
  const teamRequired = TEAM_REQUIRED_TYPES.includes(values.user_type);
  const requiredConsentComplete =
    values.terms_accepted && values.privacy_accepted;

  // Render the Turnstile widget only when a public site key is configured.
  React.useEffect(() => {
    if (!SITE.turnstileSiteKey || !turnstileRef.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || widgetIdRef.current != null) return;
        widgetIdRef.current = ts.render(turnstileRef.current, {
          sitekey: SITE.turnstileSiteKey,
          theme: "dark",
          callback: (token) => {
            turnstileTokenRef.current = token;
          },
          "expired-callback": () => {
            turnstileTokenRef.current = "";
          },
          "error-callback": () => {
            turnstileTokenRef.current = "";
          },
        });
      })
      .catch(() => {
        // Widget unavailable — server still validates and fails closed.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const markStarted = () => {
    if (!startedAtRef.current) startedAtRef.current = new Date().toISOString();
  };

  const set = (name, value) => {
    markStarted();
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));

    // A duplicate response applies only to the email that was submitted.
    // Clear that stale notice as soon as the visitor edits the address.
    if (name === "email") {
      setState((current) => (current === "duplicate" ? null : current));
    }
  };

  const validate = () => {
    const errs = {};
    const name = values.full_name.trim();
    if (name.length < 2) errs.full_name = "Please enter your full name.";
    else if (name.length > 80) errs.full_name = "Name is too long.";
    const email = values.email.trim();
    if (!email) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email) || email.length > 254)
      errs.email = "Please enter a valid email.";
    if (!values.country_code) errs.country_code = "Please choose your country.";
    if (!values.user_type) errs.user_type = "Please choose a user type.";
    if (!values.main_use_case)
      errs.main_use_case = "Please choose a use case.";
    if (teamRequired && !values.team_size)
      errs.team_size = "Please choose a team size.";
    if (!termsReviewed || !values.terms_accepted)
      errs.terms_accepted = "Please review and accept the Terms of Service.";
    if (!privacyReviewed || !values.privacy_accepted)
      errs.privacy_accepted = "Please review and acknowledge the Privacy Policy.";
    return errs;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    document.getElementById(`rq-${firstKey}`)?.focus?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState(null);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    markStarted();
    setSubmitting(true);
    const payload = {
      full_name: values.full_name.trim(),
      email: values.email.trim(),
      country_code: values.country_code,
      user_type: values.user_type,
      main_use_case: values.main_use_case,
      team_size: teamRequired ? values.team_size : null,
      terms_accepted: values.terms_accepted,
      privacy_accepted: values.privacy_accepted,
      privacy_version: SITE.privacyVersion,
      marketing_opt_in: values.marketing_opt_in,
      locale: getLocale(),
      landing_page_path: getPagePath(),
      ...getUtmParams(),
      session_id: getSessionId(),
      company: values.company, // honeypot
      started_at: startedAtRef.current,
      turnstile_token: turnstileTokenRef.current || "",
    };
    try {
      const { status, body } = await submitEarlyAccess(
        SITE.earlyAccessApiUrl,
        payload
      );
      if (status === 201) {
        // Only a real 201 clears the form.
        setValues(initial);
        setErrors({});
        setState("success");
        setTermsReviewed(false);
        setPrivacyReviewed(false);
        startedAtRef.current = null;
        turnstileTokenRef.current = "";
        resetSessionId();
        try {
          if (window.turnstile && widgetIdRef.current != null) {
            window.turnstile.reset(widgetIdRef.current);
          }
        } catch {
          // reset best-effort
        }
      } else if (status === 409) {
        setState("duplicate");
        setErrors({ email: "This email is already registered." });
        focusFirstError({ email: true });
      } else if (status === 422) {
        const fields = body?.error?.fields || {};
        setErrors((prev) => ({ ...prev, ...fields }));
        setState("error");
        focusFirstError(fields);
        summaryRef.current?.focus?.();
      } else if (status === 429) {
        setState("rate_limited");
      } else if (status === 503) {
        setState("unavailable");
      } else {
        setState("error");
      }
    } catch {
      setState("error");
    } finally {
      setSubmitting(false);
    }
  };

  const errorList = Object.entries(errors).filter(([, msg]) => Boolean(msg));

  return (
    <section
      id="request"
      className="z-section-compact"
      data-testid="request-form-section"
    >
      <div className="z-page">
        <div className="max-w-[820px] mx-auto z-frame p-6 md:p-10">
          <Heading
            className="font-display text-[28px] md:text-[36px] leading-[1.1]"
            data-testid="request-heading"
          >
            Request Early Access
          </Heading>
          <p className="mt-3 text-[hsl(var(--z-text-muted))]">
            Tell us how you plan to use Culmora OS.
          </p>

          {state === "success" ? (
            <div
              role="status"
              aria-live="polite"
              className="mt-8 rounded-2xl border border-[hsl(var(--z-teal))]/40 bg-[hsl(var(--z-teal-soft))] px-6 py-8 text-center"
              data-testid="rq-success"
            >
              <CheckCircle2
                size={28}
                className="mx-auto text-[hsl(var(--z-teal))]"
                aria-hidden="true"
              />
              <p className="mt-4 font-display text-[22px] text-[hsl(var(--z-text))]">
                Request received.
              </p>
              <p className="mt-2 text-[14.5px] text-[hsl(var(--z-text-muted))] max-w-[46ch] mx-auto">
                Thank you — your early-access request has been recorded. We will
                only use your details to follow up about Culmora OS availability.
              </p>
            </div>
          ) : (
            <form
              noValidate
              onSubmit={onSubmit}
              autoComplete="off"
              data-testid="request-form"
              className="mt-8"
            >
              {errorList.length > 0 && (
                <div
                  role="alert"
                  tabIndex={-1}
                  ref={summaryRef}
                  className="mb-6 rounded-xl border border-[hsl(var(--z-error))]/50 bg-[hsl(var(--z-error))]/10 px-4 py-3"
                  data-testid="rq-error-summary"
                >
                  <p className="text-[14px] font-medium text-[hsl(var(--z-text))]">
                    Please review the following:
                  </p>
                  <ul className="mt-2 space-y-1 text-[13.5px] text-[hsl(var(--z-error))]">
                    {errorList.map(([key, msg]) => (
                      <li key={key}>
                        <a href={`#rq-${key}`} className="underline">
                          {msg}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <Field id="rq-full_name" label="Full name" error={errors.full_name}>
                  <input
                    id="rq-full_name"
                    type="text"
                    className={inputCls}
                    value={values.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    aria-invalid={Boolean(errors.full_name)}
                    maxLength={80}
                    data-testid="rq-full-name"
                  />
                </Field>
                <Field id="rq-email" label="Email" error={errors.email}>
                  <input
                    id="rq-email"
                    type="email"
                    inputMode="email"
                    className={inputCls}
                    value={values.email}
                    onChange={(e) => set("email", e.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    maxLength={254}
                    data-testid="rq-email"
                  />
                </Field>
                <Field id="rq-country_code" label="Country" error={errors.country_code}>
                  <select
                    id="rq-country_code"
                    className={inputCls}
                    value={values.country_code}
                    onChange={(e) => set("country_code", e.target.value)}
                    aria-invalid={Boolean(errors.country_code)}
                    data-testid="rq-country"
                  >
                    <option value="" disabled>
                      Select country
                    </option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="rq-user_type" label="User type" error={errors.user_type}>
                  <select
                    id="rq-user_type"
                    className={inputCls}
                    value={values.user_type}
                    onChange={(e) => set("user_type", e.target.value)}
                    aria-invalid={Boolean(errors.user_type)}
                    data-testid="rq-user-type"
                  >
                    <option value="" disabled>
                      Select user type
                    </option>
                    {USER_TYPES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="rq-main_use_case"
                  label="Main use case"
                  error={errors.main_use_case}
                >
                  <select
                    id="rq-main_use_case"
                    className={inputCls}
                    value={values.main_use_case}
                    onChange={(e) => set("main_use_case", e.target.value)}
                    aria-invalid={Boolean(errors.main_use_case)}
                    data-testid="rq-main-use"
                  >
                    <option value="" disabled>
                      Select main use case
                    </option>
                    {MAIN_USE_CASES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {teamRequired && (
                  <Field id="rq-team_size" label="Team size" error={errors.team_size}>
                    <select
                      id="rq-team_size"
                      className={inputCls}
                      value={values.team_size}
                      onChange={(e) => set("team_size", e.target.value)}
                      aria-invalid={Boolean(errors.team_size)}
                      data-testid="rq-team-size"
                    >
                      <option value="" disabled>
                        Select team size
                      </option>
                      {TEAM_SIZES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>

              {/* Honeypot — visually hidden, must remain empty */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "-9999px",
                  height: 0,
                  overflow: "hidden",
                }}
              >
                <label htmlFor="rq-company">Company</label>
                <input
                  id="rq-company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.company}
                  onChange={(e) => set("company", e.target.value)}
                  data-testid="rq-honeypot"
                />
              </div>

              <div className="mt-7 space-y-5" data-testid="rq-legal-review">
                <div>
                  <p className="text-[12px] uppercase tracking-[0.16em] text-[hsl(var(--z-text-soft))] mb-2">
                    Required legal review
                  </p>
                  <p className="text-[13.5px] text-[hsl(var(--z-text-muted))]">
                    Review each notice below. Its consent checkbox unlocks after
                    you reach the end of that notice.
                  </p>
                </div>

                <LegalReviewPanel
                  title="Terms of Service"
                  fullHref="/terms"
                  reviewed={termsReviewed}
                  onReviewed={() => setTermsReviewed(true)}
                  testId="rq-terms-review"
                >
                  <p>Culmora OS is currently a pre-launch product. Features, availability and product details may change before release.</p>
                  <p>An Early Access request records your interest only. It does not create an account, guarantee access, or create a paid service contract.</p>
                  <p>No payment is collected through this landing-page request form. Any future product access will be subject to the applicable product terms presented at that time.</p>
                  <p>Do not misuse, automate fraudulent submissions to, or interfere with this website or its request systems.</p>
                  <p>Culmora OS branding, screenshots and original materials remain protected intellectual property. Unauthorised copying, impersonation or misuse is prohibited.</p>
                  <p>The site is provided on a pre-launch basis, without warranties to the maximum extent permitted by applicable law. The full Terms contain the governing-law and contact provisions.</p>
                  <p className="font-medium text-[hsl(var(--z-text))]">You have reached the end of this Terms review.</p>
                </LegalReviewPanel>

                <label className="flex items-start gap-3 text-[14px] text-[hsl(var(--z-text-muted))]">
                  <input
                    id="rq-terms_accepted"
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-[hsl(var(--z-border-strong))] bg-transparent accent-[hsl(var(--z-teal))] disabled:opacity-40"
                    checked={values.terms_accepted}
                    disabled={!termsReviewed}
                    onChange={(e) => set("terms_accepted", e.target.checked)}
                    aria-invalid={Boolean(errors.terms_accepted)}
                    data-testid="rq-terms"
                  />
                  <span>
                    I have reviewed and accept the{" "}
                    <a href="/terms" className="text-[hsl(var(--z-teal))] underline">
                      Terms of Service
                    </a>
                    .
                  </span>
                </label>
                {!termsReviewed && (
                  <p className="text-[12.5px] text-[hsl(var(--z-text-soft))]">
                    Scroll the Terms review to the end to enable this checkbox.
                  </p>
                )}
                {errors.terms_accepted && (
                  <p className="text-[13px] text-[hsl(var(--z-error))]" role="alert">
                    {errors.terms_accepted}
                  </p>
                )}

                <LegalReviewPanel
                  title="Privacy Policy"
                  fullHref="/privacy"
                  reviewed={privacyReviewed}
                  onReviewed={() => setPrivacyReviewed(true)}
                  testId="rq-privacy-review"
                >
                  <p>The request form collects your name, email, country, user type, main use case, team size when applicable, legal consent choices, locale, page path, campaign parameters and a session identifier.</p>
                  <p>Your request is sent to a protected server-side submission function and stored in an access-controlled waitlist table within Culmora's Supabase infrastructure. The public browser cannot write directly to that table.</p>
                  <p>Submissions are validated and rate-limited. For abuse prevention, request IP information may be converted server-side into a one-way keyed hash; the raw IP is not stored in the waitlist record.</p>
                  <p>Your information is used to manage Early Access. Product and availability updates are sent only when you separately choose the optional marketing consent below.</p>
                  <p>Culmora OS does not sell this request information and does not share it with advertisers. You may request access, correction or deletion using the contact details in the full Privacy Policy.</p>
                  <p>The full Privacy Policy explains storage, security, rights, tracking status and policy updates in more detail.</p>
                  <p className="font-medium text-[hsl(var(--z-text))]">You have reached the end of this Privacy review.</p>
                </LegalReviewPanel>

                <label className="flex items-start gap-3 text-[14px] text-[hsl(var(--z-text-muted))]">
                  <input
                    id="rq-privacy_accepted"
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-[hsl(var(--z-border-strong))] bg-transparent accent-[hsl(var(--z-teal))] disabled:opacity-40"
                    checked={values.privacy_accepted}
                    disabled={!privacyReviewed}
                    onChange={(e) => set("privacy_accepted", e.target.checked)}
                    aria-invalid={Boolean(errors.privacy_accepted)}
                    data-testid="rq-privacy"
                  />
                  <span>
                    I have reviewed and acknowledge the{" "}
                    <a href="/privacy" className="text-[hsl(var(--z-teal))] underline">
                      Privacy Policy
                    </a>
                    , including the processing of my request information.
                  </span>
                </label>
                {!privacyReviewed && (
                  <p className="text-[12.5px] text-[hsl(var(--z-text-soft))]">
                    Scroll the Privacy review to the end to enable this checkbox.
                  </p>
                )}
                {errors.privacy_accepted && (
                  <p className="text-[13px] text-[hsl(var(--z-error))]" role="alert">
                    {errors.privacy_accepted}
                  </p>
                )}

                <label className="flex items-start gap-3 text-[14px] text-[hsl(var(--z-text-muted))]">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-[hsl(var(--z-border-strong))] bg-transparent accent-[hsl(var(--z-teal))]"
                    checked={values.marketing_opt_in}
                    onChange={(e) => set("marketing_opt_in", e.target.checked)}
                    data-testid="rq-marketing"
                  />
                  <span>Send me product and availability updates. Optional.</span>
                </label>
              </div>

              {SITE.turnstileSiteKey && (
                <div className="mt-6" data-testid="rq-turnstile">
                  <div ref={turnstileRef} />
                </div>
              )}

              <div className="mt-6 flex flex-col gap-4">
                <button
                  type="submit"
                  className="z-btn-primary min-w-[200px] w-fit"
                  disabled={submitting || !configured || !requiredConsentComplete}
                  aria-disabled={submitting || !configured || !requiredConsentComplete}
                  data-testid="rq-submit"
                >
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>

                {!requiredConsentComplete && configured && (
                  <p className="text-[12.5px] text-[hsl(var(--z-text-soft))]" data-testid="rq-consent-required">
                    Review and accept both required legal notices to enable submission.
                  </p>
                )}

                {!configured && (
                  <div
                    role="status"
                    className="flex items-start gap-3 rounded-xl border border-[hsl(var(--z-border-strong))] bg-[hsl(var(--z-surface))] px-4 py-3 text-[14px] text-[hsl(var(--z-text-muted))]"
                    data-testid="rq-not-configured"
                  >
                    <Info size={16} className="mt-0.5 text-[hsl(var(--z-teal))]" aria-hidden="true" />
                    <span>
                      Early-access submissions are being enabled. Please check
                      again soon.
                    </span>
                  </div>
                )}

                {state === "duplicate" && (
                  <Notice testId="rq-duplicate" role="alert">
                    This email is already registered for early access. There is
                    no need to submit again.
                  </Notice>
                )}
                {state === "rate_limited" && (
                  <Notice testId="rq-rate-limited" role="alert">
                    Too many attempts. Please wait a while before trying again.
                  </Notice>
                )}
                {state === "unavailable" && (
                  <Notice testId="rq-unavailable" role="alert">
                    Submissions are temporarily unavailable. Please try again
                    soon — your entered details are preserved.
                  </Notice>
                )}
                {state === "error" && errorList.length === 0 && (
                  <Notice testId="rq-error" role="alert">
                    Something went wrong while submitting. Your entered details
                    are preserved — please try again.
                  </Notice>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function LegalReviewPanel({
  title,
  fullHref,
  reviewed,
  onReviewed,
  testId,
  children,
}) {
  const onScroll = (event) => {
    if (reviewed) return;
    const node = event.currentTarget;
    const reachedEnd =
      node.scrollTop + node.clientHeight >= node.scrollHeight - 8;
    if (reachedEnd) onReviewed();
  };

  return (
    <section className="rounded-xl border border-[hsl(var(--z-border-strong))] bg-[hsl(var(--z-surface))] overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[hsl(var(--z-border-strong))]">
        <div>
          <p className="text-[14px] font-medium text-[hsl(var(--z-text))]">
            {title} <span className="text-[hsl(var(--z-text-soft))]">— Required</span>
          </p>
          <p className="mt-1 text-[12px] text-[hsl(var(--z-text-soft))]">
            {reviewed ? "Review completed" : "Scroll to the end to continue"}
          </p>
        </div>
        <a
          href={fullHref}
          className="shrink-0 text-[12.5px] text-[hsl(var(--z-teal))] underline"
        >
          Read full
        </a>
      </div>
      <div
        tabIndex={0}
        onScroll={onScroll}
        className="max-h-52 overflow-y-auto px-4 py-4 space-y-3 text-[13.5px] leading-6 text-[hsl(var(--z-text-muted))] focus:outline-none focus:ring-1 focus:ring-inset focus:ring-[hsl(var(--z-teal))]"
        data-testid={testId}
        aria-label={`${title} review`}
      >
        {children}
      </div>
    </section>
  );
}

function Notice({ testId, role, children }) {
  return (
    <div
      role={role}
      className="flex items-start gap-3 rounded-xl border border-[hsl(var(--z-border-strong))] bg-[hsl(var(--z-surface))] px-4 py-3 text-[14px] text-[hsl(var(--z-text-muted))]"
      data-testid={testId}
    >
      <Info size={16} className="mt-0.5 text-[hsl(var(--z-teal))]" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

const inputCls =
  "w-full min-h-[48px] rounded-xl bg-[hsl(var(--z-surface))] border border-[hsl(var(--z-border-strong))] px-4 text-[15px] text-[hsl(var(--z-text))] placeholder:text-[hsl(var(--z-text-soft))] focus:outline-none focus:border-[hsl(var(--z-teal))]";

function Field({ id, label, error, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[12px] uppercase tracking-[0.16em] text-[hsl(var(--z-text-soft))] mb-2"
      >
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-2 text-[13px] text-[hsl(var(--z-error))]" role="alert">
          <AlertTriangle size={12} className="inline mr-1 -mt-0.5" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
